/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  equalTo,
  get,
  getDatabase,
  onValue,
  orderByChild,
  push,
  query,
  ref,
  set,
  update,
} from "firebase/database";
import { useAuth } from "@/contexts/AuthContext";
import type {
  CircleInvitation,
  CircleInvitationStatus,
  CircleRole,
  PrivacyLevel,
  ProtectedProfile,
  Relationship,
  SafeCircleMode,
} from "@/types/safecircle";

const INVITATION_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

export interface SendInviteArgs {
  profile: ProtectedProfile;
  toName: string;
  toPhone?: string;
  toEmail?: string;
  circleRole: CircleRole;
  relationship?: Relationship;
  subGroup?: string;
  privacyLevel: PrivacyLevel;
  mode: SafeCircleMode;
  toUid?: string;
}

export function useCircleInvitations() {
  const { user } = useAuth();
  const [incoming, setIncoming] = useState<CircleInvitation[]>([]);
  const [outgoing, setOutgoing] = useState<CircleInvitation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setIncoming([]);
      setOutgoing([]);
      setLoading(false);
      return;
    }
    const db = getDatabase();
    const toQuery = query(
      ref(db, "circleInvitations"),
      orderByChild("toUid"),
      equalTo(user.uid),
    );
    const fromQuery = query(
      ref(db, "circleInvitations"),
      orderByChild("fromUid"),
      equalTo(user.uid),
    );
    let pending = 2;
    const done = () => {
      pending -= 1;
      if (pending <= 0) setLoading(false);
    };
    const unsubIn = onValue(toQuery, (snap) => {
      const data = snap.val();
      const list = data
        ? Object.entries(data).map(
            ([id, val]) => ({ ...(val as CircleInvitation), invitationId: id }),
          )
        : [];
      setIncoming(list.filter((i) => i.status === "pending"));
      done();
    });
    const unsubOut = onValue(fromQuery, (snap) => {
      const data = snap.val();
      const list = data
        ? Object.entries(data).map(
            ([id, val]) => ({ ...(val as CircleInvitation), invitationId: id }),
          )
        : [];
      setOutgoing(list);
      done();
    });
    return () => {
      unsubIn();
      unsubOut();
    };
  }, [user]);

  const sendInvite = useCallback(
    async (args: SendInviteArgs): Promise<string | null> => {
      if (!user) return null;
      const db = getDatabase();
      const inviteRef = push(ref(db, "circleInvitations"));
      const invitationId = inviteRef.key!;
      const now = Date.now();

      let resolvedUid = args.toUid;
      if (!resolvedUid && args.toPhone) {
        try {
          const phoneQuery = query(
            ref(db, "users"),
            orderByChild("profile/phone"),
            equalTo(args.toPhone),
          );
          const snap = await get(phoneQuery);
          const data = snap.val();
          if (data) {
            resolvedUid = Object.keys(data)[0];
          }
        } catch {
          // best-effort; leave unresolved
        }
      }

      const invitation: CircleInvitation = {
        invitationId,
        fromUid: user.uid,
        fromName: user.displayName || user.email || "Someone",
        fromProfileId: args.profile.profileId,
        fromProfileName: args.profile.name,
        toName: args.toName,
        toPhone: args.toPhone,
        toEmail: args.toEmail,
        toUid: resolvedUid,
        mode: args.mode,
        circleRole: args.circleRole,
        relationship: args.relationship,
        subGroup: args.subGroup,
        privacyLevel: args.privacyLevel,
        status: "pending",
        createdAt: now,
        expiresAt: now + INVITATION_TTL_MS,
      };
      await set(inviteRef, invitation);
      return invitationId;
    },
    [user],
  );

  const respond = useCallback(
    async (invitationId: string, status: CircleInvitationStatus) => {
      if (!user) return;
      const db = getDatabase();
      await update(ref(db, `circleInvitations/${invitationId}`), {
        status,
        respondedAt: Date.now(),
      });
    },
    [user],
  );

  const accept = useCallback(
    (invitationId: string) => respond(invitationId, "accepted"),
    [respond],
  );

  const decline = useCallback(
    (invitationId: string) => respond(invitationId, "declined"),
    [respond],
  );

  const pendingIncoming = useMemo(
    () => incoming.filter((i) => i.status === "pending" && i.expiresAt > Date.now()),
    [incoming],
  );

  return {
    incoming: pendingIncoming,
    outgoing,
    loading,
    sendInvite,
    accept,
    decline,
  };
}
